/**
 * Static display info (name + icon) for client built-in connectors —
 * `connectionProviders/*` ids like `connectionProviders/request` or
 * `connectionProviders/agent`.
 *
 * These connectors have no metadata endpoint to fetch
 * (`isUnfetchableConnectorId` in useConnectionMutations gates them out — and
 * its check expects a leading slash, which template-authored ids don't carry),
 * but every curated client operation already embeds its connector block under
 * `properties.api` with a canonical displayName and iconUri. Resolving from
 * that static set avoids any network fetch for built-ins.
 */

import { getClientBuiltInOperations } from '@/data/clientOperations';

export interface BuiltInConnectorDisplayInfo {
  displayName: string;
  iconUri: string;
}

let cachedMap: Map<string, BuiltInConnectorDisplayInfo> | null = null;

/** Normalize a connector id for lookup: strip leading slash, lowercase. */
function normalizeKey(connectorId: string): string {
  return connectorId.replace(/^\/+/, '').toLowerCase();
}

function buildMap(): Map<string, BuiltInConnectorDisplayInfo> {
  const map = new Map<string, BuiltInConnectorDisplayInfo>();
  for (const operation of getClientBuiltInOperations()) {
    const api = operation.properties.api;
    if (!api?.id || !api.displayName) continue;
    const key = normalizeKey(api.id);
    if (!map.has(key)) {
      map.set(key, { displayName: api.displayName, iconUri: api.iconUri ?? '' });
    }
  }
  return map;
}

/**
 * Look up static display info for a client built-in connector id. Accepts
 * both `connectionProviders/request` and `/connectionProviders/request`
 * forms, case-insensitively. Returns undefined for ids outside the curated
 * client operation set (managed APIs, service providers, …).
 */
export function getBuiltInConnectorDisplayInfo(
  connectorId: string,
): BuiltInConnectorDisplayInfo | undefined {
  cachedMap ??= buildMap();
  return cachedMap.get(normalizeKey(connectorId));
}
